import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import ControlPanelSidebar from "../components/control-panel/ControlPanelSidebar";
import { RawCell } from "../models/collaborate.model";
import {
    NodeState,
    selectSelectedCell,
    setControlPanelStatus,
    setSelectedAgencyToolIndex,
    setSelectedCellId,
    setSelectedControlPanelComponent,
    setSelectedWishedToolId,
    updateCell
} from "../store/slices/node.slice";
import { ILGraphNode } from "../types/litegraph-update";
import { cellsSetupTools } from "../utils/cell-setup";
import useGlobalVariables from "../utils/global-variables";

const ToolsView = () => {
    const dispatch = useDispatch();
    const { editor, LiteGraph, LGraphGroup } = useGlobalVariables();
    const location = useLocation();
    const [cellId, setCellId] = useState<string>("");
    const navigate = useNavigate();
    const selectedCell = useSelector(selectSelectedCell);
    const toolsNodeList = useRef<ILGraphNode[]>([]);
    const [graphLoaded, setGraphLoaded] = useState(false);
    const selectedCellRef = useRef(selectedCell);
    const nodesDataState: NodeState = useSelector((state: any) => state.nodeData);
    const isRigidCell = nodesDataState.collabApp?.type === "rigid";

    useEffect(() => {
        selectedCellRef.current = selectedCell;
    }, [selectedCell]);

    useEffect(() => {
        if (LiteGraph) {
            cellsSetupTools(LiteGraph);
        }
    }, [LiteGraph]);

    const getMaxCellIdentifier = (cell: RawCell) => {
        return Math.max(...cell.tool_wishlist.map((tool) => tool.tool_identifier));
    };

    useEffect(() => {
        if (editor && !graphLoaded) {
            // Clear graph
            editor.graphcanvas.graph.clear();

            // On Node Add
            editor.graphcanvas.graph.onNodeAdded = function (node: ILGraphNode) {
                if (node.type === "Capabilities/ToolsNode") {
                    if (!node.properties?.identifier && selectedCellRef.current) {
                        // console.log(node);
                        node.properties = {
                            identifier: getMaxCellIdentifier(selectedCellRef.current) + 1
                        };

                        node.addWidget("combo", "Tool", "", () => {}, {
                            values: ["None"]
                        });

                        // Add the tool to the tool_wishlist of the cell in the redux store
                        dispatch(
                            updateCell({
                                unique_identifier: selectedCellRef.current.unique_identifier,
                                tool_wishlist: [
                                    ...selectedCellRef.current.tool_wishlist,
                                    {
                                        tool_identifier:
                                            getMaxCellIdentifier(selectedCellRef.current) + 1,
                                        goal: "",
                                        title: "",
                                        description: "",
                                        selected_tool: {},
                                        tool_replacements: [],
                                        require_previous_output: []
                                    }
                                ]
                            })
                        );
                    }

                    node.onDblClick = (e) => {
                        e.preventDefault();
                        dispatch(setSelectedWishedToolId(node.properties?.identifier));
                        /**
                         * Open sidebar
                         */
                        dispatch(setControlPanelStatus("warning"));
                        dispatch(setSelectedControlPanelComponent("tools"));
                    };

                    // Connect to previous tool
                    // console.log(toolsNodeList);
                    toolsNodeList.current.at(-1)?.connect(0, node, 0);

                    toolsNodeList.current.push(node);

                    // editor.graphcanvas.setDirty(true, true);
                } else if (node.type === "Main/AgencyToolNode") {
                    // Connect to previous tool
                    toolsNodeList.current.at(-1)?.connect(0, node, 0);

                    toolsNodeList.current.push(node);

                    node.onDblClick = (e) => {
                        e.preventDefault();
                        dispatch(setSelectedAgencyToolIndex(node.properties?.identifier));
                        /**
                         * Open sidebar
                         */
                        dispatch(setControlPanelStatus("warning"));
                        dispatch(setSelectedControlPanelComponent("agencyTools"));
                    };
                } else if (node.type === "Main/RigidAppToolNode") {
                    // Connect to previous tool
                    toolsNodeList.current.at(-1)?.connect(0, node, 0);

                    toolsNodeList.current.push(node);

                    node.onDblClick = (e) => {
                        e.preventDefault();

                        /**
                         * Route to Tools display view
                         */
                        // Check if tool has display nodes
                        if (
                            nodesDataState.cells?.[0]?.tool_wishlist?.find(
                                (tool) => tool.tool_identifier === node.properties?.identifier
                            )?.display?.nodes?.length
                        ) {
                            const urlParams = new URLSearchParams(location.search);

                            urlParams.set("displayed_tool", node.properties?.identifier);

                            navigate(`/capabilities/tool-display?${urlParams.toString()}`);
                        } else {
                            e.preventDefault();
                            dispatch(setSelectedAgencyToolIndex(node.properties?.identifier));
                            /**
                             * Open sidebar
                             */
                            dispatch(setControlPanelStatus("warning"));
                            dispatch(setSelectedControlPanelComponent("agencyTools"));
                        }
                    };
                }
            };

            setGraphLoaded(true);
        }

        // Set cell Id
        if (location.search) {
            const urlParams = new URLSearchParams(location.search);
            const cellId = urlParams.get("cell");
            if (cellId) {
                setCellId(cellId);
                // set selected cell in state
                dispatch(setSelectedCellId(parseInt(cellId)));
            }
        }
    }, [editor, location.search]);

    useEffect(() => {
        /**
         * In the case of rigid collab app group, create a group with perceptor input and activator endpoint
         * Create custom nodes for rigid type tools
         * Get the corresponding arguments of the display tools from the cell[0].tool_wishlist[0].selected_tool
         * When the arguments are updates, update the correspond `default_value` of the arguments of the selected_tool.
         *
         * If the tool type is not rigid, construct the nodes with the tool_wishlist
         */
        if (LiteGraph && editor) {
            console.log("Initialized");
            if (nodesDataState.collabApp?.type === "rigid") {
                const group = new LGraphGroup();

                let nodeSize = [0, 0],
                    toolStartingPositionX = 700,
                    toolStartingPositionY = 250;
                const startingPositionX = 250,
                    startingPosisionY = 150,
                    agencyToolWidth = 300,
                    agencyToolHeight = 45,
                    displayTools = nodesDataState.collabApp.json_structure.display?.tools || [],
                    cellTools = nodesDataState.cells?.[0]?.tool_wishlist || [],
                    toolsXOffset = 50,
                    toolsYOffset = 50;

                /**
                 * Make the height of the group be equal to;
                 * = height of top padding + node height + toolsYOffset + plus bottom padding
                 */
                group.pos = [startingPositionX + 400, startingPosisionY - 10];
                group.size = [
                    50 +
                        (agencyToolWidth + toolsXOffset) *
                            (displayTools.length || cellTools.length),
                    100 +
                        (agencyToolHeight + toolsYOffset) *
                            (displayTools.length || cellTools.length) +
                        50
                ]; // Set the size of the group
                group.title = "Rigid Flow";
                group.removable = false;

                editor?.graph.add(group);

                // create nodes
                const perceptorNode = LiteGraph.createNode("Main/PerceptorInputNode");

                perceptorNode.pos = [420, 200];

                perceptorNode.title = "Perceptor Input";

                editor?.graph.add(perceptorNode);

                // Create activator
                const activatorNode = LiteGraph.createNode("Main/ActivatorNode");

                activatorNode.pos = [
                    group.pos[0] + group.size[0] + 100,
                    group.pos[1] + group.size[1] - 20
                ];

                activatorNode.title = "Activator";

                editor?.graph.add(activatorNode);

                if (displayTools.length > 0) {
                    // Construct the display tools
                    // For backward compatibility
                    for (const [index, tool] of displayTools.entries()) {
                        const toolsNode = LiteGraph.createNode("Main/AgencyToolNode");
                        toolsNode.pos = [toolStartingPositionX, toolStartingPositionY];

                        toolsNode.title = tool.title;
                        toolsNode.inputs[0].label =
                            tool.description.length > 45
                                ? tool.description.slice(0, 43) + ".."
                                : tool.description;

                        toolsNode.properties = {
                            identifier: index
                        };

                        // toolsNode.removable = false;
                        editor?.graph.add(toolsNode);

                        nodeSize = toolsNode.size;

                        toolStartingPositionX += nodeSize[0] + toolsXOffset;
                        toolStartingPositionY += nodeSize[1] + toolsYOffset;

                        // connect perceptor node to first agency tool node
                        if (index === 0) {
                            perceptorNode.connect(0, toolsNode, 0);
                        }
                        // connect last agency tool node to activator node
                        if (index === displayTools.length - 1) {
                            toolsNode.connect(0, activatorNode, 0);
                        }

                        if (index > 0) {
                            // console.log(toolsNodeList, index + index);
                            // if not a ridig cell, connect the tools
                            if (!isRigidCell) {
                                toolsNodeList.current[index - 1].connect(0, toolsNode, 0);
                            }
                            // toolsNodeList.current[index + index].connect(0, toolsNode, 0);
                        }
                    }
                } else {
                    // Construct the real tools
                    for (const [index, tool] of cellTools.entries()) {
                        const toolsNode = LiteGraph.createNode("Main/RigidAppToolNode");
                        toolsNode.pos = [toolStartingPositionX, toolStartingPositionY];

                        toolsNode.title =
                            tool.title ||
                            (tool.selected_tool.display_name
                                ? tool.selected_tool.display_name.slice(0, 33) + ".."
                                : tool.selected_tool.tool_name.slice(0, 33) + "..");
                        toolsNode.inputs[0].label =
                            tool.description?.length > 45
                                ? tool.description.slice(0, 43) + ".."
                                : tool.selected_tool?.description.slice(0, 43) + "..";

                        toolsNode.properties = {
                            identifier: tool.tool_identifier
                        };

                        // toolsNode.removable = false;
                        editor?.graph.add(toolsNode);

                        nodeSize = toolsNode.size;

                        toolStartingPositionX += nodeSize[0] + toolsXOffset;
                        toolStartingPositionY += nodeSize[1] + toolsYOffset;

                        // connect perceptor node to first agency tool node
                        if (index === 0) {
                            perceptorNode.connect(0, toolsNode, 0);
                        }
                        // connect last agency tool node to activator node
                        if (index === cellTools.length - 1) {
                            toolsNode.connect(0, activatorNode, 0);
                        }

                        if (index > 0) {
                            // console.log(toolsNodeList, index + index);
                            // if not a ridid cell, connect the tools
                            if (!isRigidCell) {
                                toolsNodeList.current[index - 1].connect(0, toolsNode, 0);
                            }
                            // toolsNodeList.current[index + index].connect(0, toolsNode, 0);
                        }
                    }
                }
            } else {
                if (toolsNodeList.current.length === 0 && selectedCell?.tool_wishlist) {
                    let nodeSize = [0, 0],
                        toolStartingPositionX = 420,
                        toolStartingPositionY = 200;

                    for (const [index, tool] of selectedCell.tool_wishlist.entries()) {
                        const toolsNode = LiteGraph.createNode("Capabilities/ToolsNode");
                        toolsNode.pos = [toolStartingPositionX, toolStartingPositionY];

                        toolsNode.title =
                            tool.title ||
                            tool.selected_tool.display_name ||
                            tool.selected_tool.tool_name;
                        toolsNode.inputs[0].label =
                            tool.description?.length > 45
                                ? tool.description.slice(0, 43) + ".."
                                : tool.selected_tool?.description;

                        toolsNode.properties = {
                            identifier: tool.tool_identifier
                        };
                        // toolsNode.removable = false;
                        editor?.graph.add(toolsNode);

                        nodeSize = toolsNode.size;

                        toolStartingPositionX += nodeSize[0] + 55;
                        toolStartingPositionY += nodeSize[1] + 80;

                        if (index > 0) {
                            toolsNodeList.current[index - 1].connect(0, toolsNode, 0);
                        }

                        toolsNode.addWidget("combo", "Tool", "", () => {}, {
                            values: tool.tool_replacements.map(
                                (replacement) => replacement.tool_name
                            )
                        });

                        // toolsNode.onMouseEnter = function () {
                        //     console.log(toolsNode.size[0]);
                        //     console.log(toolStartingPositionX);
                        //     console.log(toolStartingPositionY);
                        // };

                        // const div = document.createElement("div");
                        // const button = document.createElement("button");
                        // button.innerHTML = ">";
                        // div.className = `px-2 bg-green-500 text-white absolute`;
                        // div.appendChild(button);
                        // div.style.top = `${toolStartingPositionY}px`;
                        // div.style.left = `${toolStartingPositionX + (toolsNode.size[0] - 40)}px`;

                        // document.body.appendChild(div);

                        // toolsNodeList.current.push(toolsNode);
                        // too.connect(0, nucleusNode, 0);
                    }
                }
            }
        }
    }, [editor, nodesDataState.collabApp, dispatch]);

    const navigateToHome = () => {
        // close side nav
        dispatch(setControlPanelStatus("hidden"));
        // Navigate to home page
        navigate(`/${location.search}`);
    };

    return (
        <main>
            <ControlPanelSidebar
                breadcrumbs={[
                    {
                        label: isRigidCell ? "Rigid Cell" : `Cell ${cellId}`,
                        onClick: isRigidCell ? undefined : navigateToHome,
                        isCurrentPage: isRigidCell
                    },
                    {
                        label: isRigidCell ? nodesDataState.collabApp?.title || "" : "Capabilities",
                        isCurrentPage: true
                    }
                ]}
            />
        </main>
    );
};

export default ToolsView;
